import React from "react";
import type { TodoTemplate } from "../../../../domain/types/todo";
import { Clock, Users, Link2, GripVertical, Edit2, Trash2 } from "lucide-react";

interface TodoTemplateCardProps {
  template: TodoTemplate;
  onEdit: (template: TodoTemplate) => void;
  onDelete: (template: TodoTemplate) => void;
  onToggleActive: (template: TodoTemplate) => void;
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const TodoTemplateCard: React.FC<TodoTemplateCardProps> = ({
  template,
  onEdit,
  onDelete,
  onToggleActive,
  isDragging = false,
  dragHandleProps,
}) => {
  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}분`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
  };

  return (
    <div
      className={`bg-white rounded-lg border ${
        isDragging ? "border-blue-400 shadow-lg" : "border-gray-200"
      } p-4 transition-all hover:shadow-md ${
        !template.isActive ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <div
          {...dragHandleProps}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 mt-1"
        >
          <GripVertical size={18} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-gray-900 truncate">{template.name}</h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Active Toggle */}
              <button
                onClick={() => onToggleActive(template)}
                className={`px-2 py-1 text-xs rounded-full ${
                  template.isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {template.isActive ? "활성" : "비활성"}
              </button>
            </div>
          </div>

          {/* Description */}
          {template.description && (
            <p className="text-sm text-gray-500 mb-3 line-clamp-2">
              {template.description}
            </p>
          )}

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            {/* Estimated Time */}
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{formatMinutes(template.estimatedMinutes)}</span>
            </div>

            {/* Default Assignees */}
            {template.defaultAssignees.length > 0 && (
              <div className="flex items-center gap-1">
                <Users size={14} />
                <span>{template.defaultAssignees.length}명</span>
              </div>
            )}

            {/* Dependencies */}
            {template.dependencies.length > 0 && (
              <div className="flex items-center gap-1">
                <Link2 size={14} />
                <span>의존성 {template.dependencies.length}개</span>
              </div>
            )}
          </div>

          {/* Dependencies Preview */}
          {template.dependencies.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {template.dependencies.map((dep) => (
                <span
                  key={dep.id}
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                    dep.dependencyType === "BLOCKING"
                      ? "bg-red-100 text-red-700"
                      : dep.dependencyType === "WARNING"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {dep.dependsOnTemplateName}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            onClick={() => onEdit(template)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="편집"
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => onDelete(template)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="삭제"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TodoTemplateCard;
